/**
 * Analytics Engine — Semantic Layer
 *
 * Golden Rules:
 * 1. Category Abstraction: classify by finished/canceled booleans, never by name
 * 2. ID-Based: reference statuses by id, never by name string matching
 * 3. Runtime Discovery: all dictionaries fetched from API, zero hardcode
 * 4. Event Sourcing: pm_entity_changes for historical analytics
 * 5. Semantic Layer: one hook translates business queries to API calls
 */

import { useMemo, useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import axiosServices from "@pma/utils/axios"
import {
  STATUS_DICTIONARY_ENDPOINT,
  PRIORITY_DICTIONARY_ENDPOINT,
  ALL_ORG_USERS_ENDPOINT,
  GET_WORK_DIARY_STATUS,
  GET_DICTIONARY_RESOURCES_ENDPOINT,
  GET_WEATHER,
  PROJECT_WORK_TYPES_DICTIONARY_ENDPOINT,
} from "@pma/utils/api-routes"
import { useAuth } from "@pma/stores/auth-store"
import { CHART_PALETTE } from "@pma/utils/constants"
import { saturateForChart } from "@pma/utils/helpers"
import { DictDiaryStatus, DictPriority, DictResource, DictStatus, DictWeather, DictWorkType, OrgUser, StatusCategory } from "@pma/types"

// ---------------------------------------------------------------------------
// Types — mirrors what Xano returns
// ---------------------------------------------------------------------------



/**
 * Classify a status into open/completed/canceled.
 * Priority: finished/canceled booleans (from dictionary) → param_name fallback.
 * The embedded _status on tasks often lacks finished/canceled booleans,
 * so param_name fallback is essential for correct classification.
 */
export function classifyStatus(status: Partial<DictStatus> | null | undefined): StatusCategory {
  if (!status) return "open"
  // Primary: boolean flags (from dictionary table)
  if (status.finished === true) return "completed"
  if (status.canceled === true || status.cancelled === true) return "canceled"
  // Fallback: param_name (when booleans are missing from embedded _status)
  const pn = ((status as any)?.param_name || "").toLowerCase()
  if (pn === "done" || pn === "closed" || pn === "completed") return "completed"
  if (pn === "cancelled" || pn === "canceled") return "canceled"
  return "open"
}

// Category display — colors and labels are stable (these are OUR categories, not API statuses)
const CATEGORY_META: Record<StatusCategory, { color: string; labelEn: string; labelHe: string }> = {
  open: { color: "#3b82f6", labelEn: "Open", labelHe: "פתוח" },
  completed: { color: "#10b981", labelEn: "Completed", labelHe: "הושלם" },
  canceled: { color: "#9ca3af", labelEn: "Canceled", labelHe: "בוטל" },
}

export function getCategoryMeta(category: StatusCategory) {
  return CATEGORY_META[category]
}

// ---------------------------------------------------------------------------
// Status Display — uses API color/name, never hardcoded
// ---------------------------------------------------------------------------

export interface StatusDisplay {
  id: number
  name: string
  color: string
  category: StatusCategory
}

export function getStatusDisplay(
  statusId: number,
  statuses: DictStatus[],
): StatusDisplay {
  const s = statuses.find((st) => st.id === statusId)
  if (!s) {
    return { id: statusId, name: `Status ${statusId}`, color: "#64748b", category: "open" }
  }
  return {
    id: s.id,
    name: s.name,
    color: s.color || "#64748b",
    category: classifyStatus(s),
  }
}

/**
 * Resolve status from a task/project entity.
 *
 * Resilient approach — works regardless of what the backend returns:
 * 1. Always classify from whatever data is on the entity (_status object)
 * 2. Enrich with dictionary if available (for finished/canceled booleans)
 * 3. Never depend on dictionary being loaded (CORS, network failures)
 * 4. Never depend on specific fields existing (_status may lack id, finished, etc.)
 */
export function resolveEntityStatus(
  entity: any,
  statuses: DictStatus[],
): StatusDisplay {
  const embedded = entity?._status || entity?.status

  // If we have an embedded status object (most common from Xano joins)
  if (embedded && typeof embedded === "object") {
    const embeddedId = embedded.id || 0
    const embeddedName = embedded.name || ""
    const embeddedColor = embedded.color || "#64748b"

    // Try dictionary lookup for richer data (finished/canceled booleans)
    if (embeddedId && statuses.length > 0) {
      const fromDict = statuses.find((s) => s.id === embeddedId)
      if (fromDict) {
        return {
          id: fromDict.id,
          name: fromDict.name,
          color: fromDict.color || embeddedColor,
          category: classifyStatus(fromDict),
        }
      }
    }

    // No dictionary match — classify from embedded data directly
    // classifyStatus checks: finished boolean → param_name fallback
    return {
      id: embeddedId,
      name: embeddedName || `Status ${embeddedId || "?"}`,
      color: embeddedColor,
      category: classifyStatus(embedded),
    }
  }

  // No embedded object — try status_id with dictionary lookup
  const statusId = entity?.status_id
  if (statusId && statuses.length > 0) {
    return getStatusDisplay(statusId, statuses)
  }

  // Last resort: status_id exists but no dictionary — unknown
  if (statusId) {
    return { id: statusId, name: `Status ${statusId}`, color: "#64748b", category: "open" }
  }

  return { id: 0, name: "Unknown", color: "#64748b", category: "open" }
}

/**
 * Generate deterministic color from a string (for entities without API color)
 */
export function hashColor(str: string): string {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return CHART_PALETTE[Math.abs(hash) % CHART_PALETTE.length]
}

// ---------------------------------------------------------------------------
// The Hook — fetches all dictionaries once, caches 5 minutes
// ---------------------------------------------------------------------------

const STALE_TIME = 5 * 60 * 1000 // 5 minutes
const GC_TIME = 10 * 60 * 1000 // 10 minutes — keep cache alive after unmount

// Module-level empty array singletons — stable references for fallback when queries are loading
const EMPTY_STATUSES: DictStatus[] = []
const EMPTY_PRIORITIES: DictPriority[] = []
const EMPTY_USERS: OrgUser[] = []
const EMPTY_DIARY_STATUSES: DictDiaryStatus[] = []
const EMPTY_RESOURCES: DictResource[] = []
const EMPTY_WEATHER: DictWeather[] = []
const EMPTY_WORK_TYPES: DictWorkType[] = []

export function useAnalyticsMetadata() {
  const orgId = useAuth((s) => s.authConfig?.last_organization_id)

  // Shared options: prevent refetch on remount when cache exists
  const shared = { staleTime: STALE_TIME, gcTime: GC_TIME, refetchOnMount: false as const }

  // Task/Project statuses
  // IMPORTANT: Xano expects context_id (not organization_id) for dictionary endpoints
  const statusesQuery = useQuery<DictStatus[]>({
    queryKey: ["dict-statuses", orgId],
    queryFn: async () => {
      const res = await axiosServices.get(STATUS_DICTIONARY_ENDPOINT, {
        params: { context_id: orgId },
      })
      return res.data || []
    },
    enabled: !!orgId,
    ...shared,
  })

  // Priorities — uses organization_id (verified: priority_GET.xs)
  const prioritiesQuery = useQuery<DictPriority[]>({
    queryKey: ["dict-priorities", orgId],
    queryFn: async () => {
      const res = await axiosServices.get(PRIORITY_DICTIONARY_ENDPOINT, {
        params: { organization_id: orgId },
      })
      return res.data || []
    },
    enabled: !!orgId,
    ...shared,
  })

  // Org users
  const usersQuery = useQuery<OrgUser[]>({
    queryKey: ["org-users", orgId],
    queryFn: async () => {
      const res = await axiosServices.get(ALL_ORG_USERS_ENDPOINT, {
        params: { organization_id: orgId },
      })
      const data = res.data
      return Array.isArray(data) ? data : data?.items || []
    },
    enabled: !!orgId,
    ...shared,
  })

  // Diary statuses — uses organization_id (verified: diary/dictionary/status_GET.xs)
  const diaryStatusesQuery = useQuery<DictDiaryStatus[]>({
    queryKey: ["dict-diary-statuses", orgId],
    queryFn: async () => {
      const res = await axiosServices.get(GET_WORK_DIARY_STATUS, {
        params: { organization_id: orgId },
      })
      return res.data || []
    },
    enabled: !!orgId,
    ...shared,
  })

  // Resources — uses organization_id (verified: diary/dictionary/resources_GET.xs)
  const resourcesQuery = useQuery<DictResource[]>({
    queryKey: ["dict-resources", orgId],
    queryFn: async () => {
      const res = await axiosServices.get(GET_DICTIONARY_RESOURCES_ENDPOINT, {
        params: { organization_id: orgId },
      })
      return res.data || []
    },
    enabled: !!orgId,
    ...shared,
  })

  // Weather — uses organization_id (verified: diary/dictionary/weather_GET.xs)
  const weatherQuery = useQuery<DictWeather[]>({
    queryKey: ["dict-weather", orgId],
    queryFn: async () => {
      const res = await axiosServices.get(GET_WEATHER, {
        params: { organization_id: orgId },
      })
      return res.data || []
    },
    enabled: !!orgId,
    ...shared,
  })

  // Work types — uses organization_id (verified: dictionary/work_type_GET.xs)
  const workTypesQuery = useQuery<DictWorkType[]>({
    queryKey: ["dict-work-types", orgId],
    queryFn: async () => {
      const res = await axiosServices.get(PROJECT_WORK_TYPES_DICTIONARY_ENDPOINT, {
        params: { organization_id: orgId },
      })
      return res.data || []
    },
    enabled: !!orgId,
    ...shared,
  })

  // Convenience: stable data arrays — use module-level singletons to avoid new refs
  const statuses = statusesQuery.data ?? EMPTY_STATUSES
  const priorities = prioritiesQuery.data ?? EMPTY_PRIORITIES
  const users = usersQuery.data ?? EMPTY_USERS
  const diaryStatuses = diaryStatusesQuery.data ?? EMPTY_DIARY_STATUSES
  const resources = resourcesQuery.data ?? EMPTY_RESOURCES
  const weather = weatherQuery.data ?? EMPTY_WEATHER
  const workTypes = workTypesQuery.data ?? EMPTY_WORK_TYPES

  // Task-only / Project-only statuses — memoized to prevent new arrays on every render
  const taskStatuses = useMemo(
    () => statuses.filter((s: DictStatus) => s.for_task !== false),
    [statuses],
  )
  const projectStatuses = useMemo(
    () => statuses.filter((s: DictStatus) => s.for_project !== false),
    [statuses],
  )

  const isLoading =
    statusesQuery.isLoading ||
    prioritiesQuery.isLoading ||
    usersQuery.isLoading

  const isError =
    statusesQuery.isError ||
    prioritiesQuery.isError ||
    usersQuery.isError

  const error =
    (statusesQuery.error as Error)?.message ||
    (prioritiesQuery.error as Error)?.message ||
    (usersQuery.error as Error)?.message ||
    null

  // Stable helper references — only recreate when statuses actually change
  const boundGetStatusDisplay = useCallback(
    (statusId: number) => getStatusDisplay(statusId, statuses),
    [statuses],
  )
  const boundResolveEntityStatus = useCallback(
    (entity: any) => resolveEntityStatus(entity, statuses),
    [statuses],
  )

  return useMemo(() => ({
    orgId,
    // Raw dictionaries
    statuses,
    taskStatuses,
    projectStatuses,
    priorities,
    users,
    diaryStatuses,
    resources,
    weather,
    workTypes,
    // Query states
    isLoading,
    isError,
    error,
    // Helpers bound to current dictionaries
    classifyStatus: (status: Partial<DictStatus> | null | undefined) => classifyStatus(status),
    getStatusDisplay: boundGetStatusDisplay,
    resolveEntityStatus: boundResolveEntityStatus,
  }), [
    orgId, statuses, taskStatuses, projectStatuses, priorities, users,
    diaryStatuses, resources, weather, workTypes,
    isLoading, isError, error,
    boundGetStatusDisplay, boundResolveEntityStatus,
  ])
}

// ---------------------------------------------------------------------------
// Aggregation helpers — used by all domain tabs
// ---------------------------------------------------------------------------

/**
 * Count items per status category (open/completed/canceled).
 * Returns { open, completed, canceled, total }.
 */
export function countByCategory(
  items: any[],
  statuses: DictStatus[],
): { open: number; completed: number; canceled: number; total: number } {
  let open = 0, completed = 0, canceled = 0
  items.forEach((item) => {
    const display = resolveEntityStatus(item, statuses)
    switch (display.category) {
      case "completed": completed++; break
      case "canceled": canceled++; break
      default: open++
    }
  })
  return { open, completed, canceled, total: items.length }
}

/**
 * Build donut data from items, grouped by their individual status (name + color from API).
 * Returns array of { name, color, count, category } sorted by count desc.
 */
export function buildStatusDonut(
  items: any[],
  statuses: DictStatus[],
): { name: string; color: string; count: number; category: StatusCategory }[] {
  const map = new Map<number, { name: string; color: string; count: number; category: StatusCategory }>()

  items.forEach((item) => {
    const display = resolveEntityStatus(item, statuses)
    const existing = map.get(display.id)
    if (existing) {
      existing.count++
    } else {
      map.set(display.id, {
        name: display.name,
        color: saturateForChart(display.color),
        count: 1,
        category: display.category,
      })
    }
  })

  return Array.from(map.values()).sort((a, b) => b.count - a.count)
}

/**
 * Group items by a key function, return sorted array of { key, count }.
 */
export function groupAndCount<T>(
  items: T[],
  keyFn: (item: T) => string,
): { key: string; count: number }[] {
  const map = new Map<string, number>()
  items.forEach((item) => {
    const key = keyFn(item)
    map.set(key, (map.get(key) || 0) + 1)
  })
  return Array.from(map.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count)
}

/**
 * Format currency for display (₪).
 */
export function formatMoney(amount: number): string {
  return `₪${amount.toLocaleString("he-IL", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}
